// Utility functions
const $ = selector => document.querySelector(selector);
const $$ = selector => document.querySelectorAll(selector);

// Common selectors
const SELECTORS = {
   wrapper: '.custom-select-wrapper',
   addToCartForm: 'form[action="/cart/add"]',
   addToCartButton: '#add-to-cart-button',
   productSwatches: '.swatches .product-switch',
   progressBar: '.progress-bar-value',
   embroiderySummary: '[data-embroidery-summary]',
   stampedWidget: '#stamped-main-widget',
   gallery: '.product-gallery',
   scrollIndicator: '.scroll-indicator-bar'
};

// Gallery Scroll Indicator
class GalleryScrollIndicator {
    constructor() {
        this.gallery = $(SELECTORS.gallery);
        this.indicator = $(SELECTORS.scrollIndicator);
        this.boundUpdateIndicator = this.updateScrollIndicator.bind(this);
        this.init();
    }

    init() {
        if (!this.gallery || !this.indicator) return;
        
        this.imageCount = this.gallery.children.length;
        this.bindEvents();
        this.updateScrollIndicator();
    }

    bindEvents() {
        this.gallery.addEventListener('scroll', this.boundUpdateIndicator);
        window.addEventListener('resize', this.boundUpdateIndicator);
    }

    updateScrollIndicator() {
        const scrollPercentage = (this.gallery.scrollLeft / (this.gallery.scrollWidth - this.gallery.clientWidth)) * 100;
        const translateX = (scrollPercentage * (this.imageCount - 1));
        this.indicator.style.transform = `translateX(${translateX}%)`;
    }

    cleanup() {
        if (!this.gallery) return;
        this.gallery.removeEventListener('scroll', this.boundUpdateIndicator);
        window.removeEventListener('resize', this.boundUpdateIndicator);
    }
}

// Product Swatch Preloader
class ProductPreloader {
    constructor() {
        this.cache = new Map();
        this.init();
    }

    init() {
        // Start preloading product swatch links
        this.preloadProductSwitches();
        // Set up intersection observer for lazy loading
        this.setupIntersectionObserver();
    }

    setupIntersectionObserver() {
        const options = {
            rootMargin: '50px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const link = entry.target;
                    this.preloadProduct(link.href);
                    observer.unobserve(link);
                }
            });
        }, options);

        $$(SELECTORS.productSwatches).forEach(link => {
            observer.observe(link);
        });
    }

    async preloadProductSwitches() {
        const links = $$(SELECTORS.productSwatches);
        // Prioritize adjacent swatches first
        const currentSwatch = $('.swatch__item--current');
        if (currentSwatch) {
            const adjacentSwatches = [
                currentSwatch.previousElementSibling,
                currentSwatch.nextElementSibling
            ].filter(Boolean);

            for (const swatch of adjacentSwatches) {
                const link = swatch.querySelector('a');
                if (link) await this.preloadProduct(link.href);
            }
        }
    }

    async preloadProduct(url) {
        if (this.cache.has(url)) return this.cache.get(url);

        try {
            const response = await fetch(url);
            const html = await response.text();
            this.cache.set(url, html);
            return html;
        } catch (error) {
            console.error('Error preloading product:', error);
            return null;
        }
    }

    getFromCache(url) {
        return this.cache.get(url);
    }
}

// Loading State Manager
class LoadingStateManager {
    constructor() {
        this.progressBar = $(SELECTORS.progressBar);
        this.container = this.progressBar?.closest('.progress-bar-container');
    }

    show() {
        if (!this.container) return;
        this.container.classList.remove('hidden');
        this.progressBar.style.width = '0%';
        // Trigger reflow
        this.progressBar.offsetHeight;
        this.progressBar.style.width = '90%';
    }

    complete() {
        if (!this.progressBar) return;
        this.progressBar.style.width = '100%';
        setTimeout(() => {
            if (this.container) this.container.classList.add('hidden');
            this.progressBar.style.width = '0%';
        }, 300);
    }
}

// Product Switcher
class ProductSwitcher {
    constructor() {
        this.preloader = new ProductPreloader();
        this.loadingState = new LoadingStateManager();
        this.bindEvents();
    }

    bindEvents() {
        document.addEventListener('click', async (e) => {
            const productSwitch = e.target.closest(SELECTORS.productSwatches);
            if (productSwitch) {
                e.preventDefault();
                await this.switchProduct(productSwitch);
            }
        });
    }

    async switchProduct(link) {
        this.loadingState.show();

        try {
            const url = link.href;
            let html = this.preloader.getFromCache(url) || await this.preloader.preloadProduct(url);

            if (!html) return;

            // Parse the new HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Update necessary elements
            this.updateMainContent(doc);
            this.updateURL(url);
            
            // Reinitialize components
            window.dispatchEvent(new CustomEvent('productSwitched'));
            window.refreshLazyImages();

            // Reinitialize third-party scripts
            this.reinitializeThirdPartyScripts();

        } catch (error) {
            console.error('Error switching product:', error);
        } finally {
            this.loadingState.complete();
        }
    }

    reinitializeThirdPartyScripts() {
        // Update Stamped widget product ID before reinitializing
        const stampedWidget = $(SELECTORS.stampedWidget);
        const swatchItem = $('.swatch__item--current');
        if (stampedWidget && swatchItem) {
            const newProductId = swatchItem.getAttribute('swatch-product-id');
            if (newProductId) {
                stampedWidget.setAttribute('data-product-id', newProductId);
            }
        }

        // Check for Stamped and reinitialize
        if (window.StampedFn) {
            if (window.StampedFn.reloadUGC) {
                window.StampedFn.reloadUGC();
            }
        }

        // Check for KiwiSizing
        if (window.ks && window.ks.loadSizing) {
            window._ks_sizingLoaded = false; // Reset the initialization flag
            const kiwiData = window.KiwiSizing?.data || {};
            window.ks.loadSizing({
                productData: kiwiData,
                options: {}
            });
        }
        
        // Check for Swym
        // if (window.SwymCallbacks && window.SwymCallbacks.onProductPageLoaded) {
        //     window.SwymCallbacks.onProductPageLoaded();
        // }
        // if (window._swat) {
        //     let swat = window._swat;
        //     swat.productEts.makeCacheStale();
        //     let pageData = swat.getCurrentPageData();
        //     if (pageData) {
        //         swat.api.trackPageview(pageData);
        //     }
        //     if (window.Shopify) {
        //         window.initSwymShopify(true); 
        //     }
        //     if (swat.ui && swat.ui.loadSecondaryStyles) {
        //         swat.ui.loadSecondaryStyles();
        //     }
        //     if (swat.initCustomLaunchPoints) {
        //         swat.initCustomLaunchPoints();
        //     }
        // }
        
        
        // If no direct reinitialization methods available, trigger a custom event
        window.dispatchEvent(new CustomEvent('thirdPartyScriptsReinit'));
    }

    updateMainContent(newDoc) {
        // Update main product section - adjust selectors as needed
        ['[data-section="main-product"]', '.product-gallery', '.product-info', '.product__mobile-header'].forEach(selector => {
            const oldContent = $(selector);
            const newContent = newDoc.querySelector(selector);
            if (oldContent && newContent) {
                oldContent.replaceWith(newContent);
            }
        });
    }

    updateURL(url) {
        window.history.pushState({ path: url }, '', url);
    }
}

// Custom Select Component
class CustomSelect {
    constructor() {
        this.boundOutsideClick = this.handleOutsideClick.bind(this);
        this.init();
    }
 
    cleanup() {
        document.removeEventListener('click', this.boundOutsideClick);
    }
 
    init() {
        const wrapper = $(SELECTORS.wrapper);
        // If no wrapper exists, this is likely a single variant product
        if (!wrapper) {
            // Enable add to cart button for single variant products
            const addToCartButton = $(SELECTORS.addToCartButton);
            if (addToCartButton && addToCartButton.disabled) {
                addToCartButton.disabled = false;
            }
            return;
        }
        
        this.cleanup();
        
        const newWrapper = wrapper.cloneNode(true);
        wrapper.parentNode.replaceChild(newWrapper, wrapper);
        
        this.elements = {
            wrapper: newWrapper,
            select: newWrapper.querySelector('.custom-select'),
            nativeSelect: newWrapper.querySelector('.native-select'),
            label: newWrapper.querySelector('.custom-select__label'),
            addToCartButton: $(SELECTORS.addToCartButton)
        };
 
        this.setupInitialState();
        this.bindEvents();
    }
 
    setupInitialState() {
        if (this.elements.addToCartButton) {
            // Only disable button if we have a variant selector and no value
            if (this.elements.nativeSelect) {
                this.elements.addToCartButton.disabled = !this.elements.nativeSelect.value;
            }
        }
    }
 
    bindEvents() {
        if (!this.elements?.wrapper) return;
        
        this.elements.wrapper.addEventListener('click', this.handleClick.bind(this));
        document.addEventListener('click', this.boundOutsideClick);
        this.elements.select.addEventListener('keydown', this.handleKeydown.bind(this));
    }
 
    handleClick(e) {
        const option = e.target.closest('.custom-select__option');
        const select = e.target.closest('.custom-select');
        
        if (select) {
            this.toggleDropdown();
        }
        
        if (option && !option.classList.contains('is-disabled')) {
            this.selectOption(option);
        }
    }
 
    handleOutsideClick(e) {
        if (!e.target.closest(SELECTORS.wrapper)) {
            this.closeDropdown();
        }
    }
 
    handleKeydown(e) {
        if (!this.elements?.select) return;
        
        const options = Array.from(this.elements.select.querySelectorAll('.custom-select__option:not(.is-disabled)'));
        const currentIndex = options.findIndex(option => option === document.activeElement);
        
        const keyActions = {
            'Enter': () => this.handleEnterKey(options),
            ' ': () => this.handleEnterKey(options),
            'Escape': () => this.closeDropdown(),
            'ArrowDown': () => this.handleArrowKey(currentIndex, 1, options),
            'ArrowUp': () => this.handleArrowKey(currentIndex, -1, options)
        };
 
        if (keyActions[e.key]) {
            e.preventDefault();
            keyActions[e.key]();
        }
    }
 
    handleEnterKey(options) {
        if (document.activeElement === this.elements.select) {
            this.toggleDropdown();
            if (this.isOpen()) {
                options[0]?.focus();
            }
        } else if (document.activeElement.classList.contains('custom-select__option')) {
            document.activeElement.click();
        }
    }
 
    handleArrowKey(currentIndex, direction, options) {
        if (!this.isOpen()) {
            this.openDropdown();
            options[0]?.focus();
        } else {
            const newIndex = currentIndex + direction;
            if (newIndex >= 0 && newIndex < options.length) {
                options[newIndex].focus();
            }
        }
    }
 
    toggleDropdown() {
        if (!this.elements?.select) return;
        this.elements.select.classList.toggle('is-open');
        this.updateAriaExpanded();
    }
 
    openDropdown() {
        if (!this.elements?.select) return;
        this.elements.select.classList.add('is-open');
        this.updateAriaExpanded();
    }
 
    closeDropdown() {
        if (!this.elements?.select) return;
        this.elements.select.classList.remove('is-open');
        this.updateAriaExpanded();
    }
 
    isOpen() {
        return this.elements?.select?.classList.contains('is-open');
    }
 
    updateAriaExpanded() {
        if (!this.elements?.select) return;
        this.elements.select.setAttribute('aria-expanded', this.isOpen());
    }
 
    selectOption(option) {
        if (!option || !this.elements?.nativeSelect) return;
        
        const value = option.dataset.value;
        const text = option.textContent.trim();
        
        this.elements.nativeSelect.value = value;
        this.elements.nativeSelect.dispatchEvent(new Event('change'));
        this.elements.label.textContent = text;
        this.closeDropdown();
        
        if (this.elements.addToCartButton) {
            this.elements.addToCartButton.disabled = !value;
        }
 
        this.updateAriaSelected(option);
    }
 
    updateAriaSelected(selectedOption) {
        if (!this.elements?.wrapper) return;
        this.elements.wrapper.querySelectorAll('.custom-select__option').forEach(opt => {
            opt.setAttribute('aria-selected', opt === selectedOption);
        });
    }
 }
 
// Cart Management
class CartManager {
    constructor() {
        console.log('CartManager initialized');
        this.bindEvents();
    }

    bindEvents() {
        console.log('Binding cart events');
        const form = $(SELECTORS.addToCartForm);
        if (form) {
            const addToCartButton = form.querySelector('button[type="submit"]');
            if (addToCartButton) {
                console.log('Add to cart button found, binding click event');
                addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
            } else {
                console.warn('Add to cart button not found in form');
            }
        } else {
            console.warn('Add to cart form not found');
        }
    }

    async handleAddToCart(e) {
        console.log('Add to cart clicked');
        e.preventDefault();
        const form = e.target.closest('form');
        if (!form) {
            console.warn('Form not found in click handler');
            return;
        }

        await this.processAddToCart(e.target, form);
    }

    async processAddToCart(button, form) {
        console.log('Processing add to cart');
        const originalButtonText = button.innerHTML;
        this.updateButtonState(button, true, '<span>Adding...</span>');

        try {
            const items = await this.buildCartItems(form);
            console.log('Items to be added to cart:', items);
            const result = await this.addItemsToCart(items);
            
            if (result) {
                console.log('Items added successfully, dispatching events');
                console.log('MiniCart instance:', window.miniCart);

                // window.miniCart?.reinitialize();
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('cart:update', {
                        detail: { items: result }
                    }));
                    console.log('cart:update event dispatched');
                    
                    window.dispatchEvent(new CustomEvent('cart:open'));
                    console.log('cart:open event dispatched');
                }, 100);
            } else {
                console.warn('Add to cart returned no result');
            }
        } catch (error) {
            console.error('Error in processAddToCart:', error);
        } finally {
            this.updateButtonState(button, false, originalButtonText);
        }
    }

    updateButtonState(button, disabled, html) {
        if (!button) {
            console.warn('Button not found for state update');
            return;
        }
        button.disabled = disabled;
        button.innerHTML = html;
        console.log(`Button state updated - disabled: ${disabled}`);
    }

    async buildCartItems(form) {
        console.log('Building cart items from form');
        const formData = new FormData(form);
        const items = [];

        // Build main product item
        const mainItem = {
            id: formData.get('id') || formData.get('id[]'),
            quantity: parseInt(formData.get('quantity')) || 1,
            properties: {}
        };

        console.log('Main item initial build:', mainItem);

        // Handle subscription
        const purchaseType = formData.get('purchase-type');
        if (purchaseType === 'subscribe') {
            const sellingPlan = formData.get('selling_plan');
            if (sellingPlan) {
                mainItem.selling_plan = sellingPlan;
                console.log('Added selling plan:', sellingPlan);
            }
        }

        // Get embroidery data if it exists
        const embroideryData = this.getEmbroideryData();
        console.log('Retrieved embroidery data:', embroideryData);
        
        if (embroideryData) {
            // Add embroidery properties to main item
            mainItem.properties = {
                ...mainItem.properties,
                ...embroideryData.properties,
                _embroidery_id: embroideryData.embroideryId
            };

            console.log('Updated main item with embroidery properties:', mainItem);

            // Add embroidery variant first if it exists
            if (embroideryData.variantId) {
                const embroideryItem = {
                    id: embroideryData.variantId,
                    quantity: 1,
                    properties: {
                        _embroidery_id: embroideryData.embroideryId
                    }
                };
                items.push(embroideryItem);
                console.log('Added embroidery item:', embroideryItem);
            }
        }

        // Add main item after embroidery item
        items.push(mainItem);
        console.log('Final items array:', items);

        return items;
    }

    getEmbroideryData() {
        console.log('Getting embroidery data');
        const summary = $(SELECTORS.embroiderySummary);
        if (!summary) {
            console.log('No embroidery summary found');
            return null;
        }

        const variantId = summary.querySelector('input[name="embroidery_variant_id"]')?.value;
        if (!variantId) {
            console.log('No embroidery variant ID found');
            return null;
        }

        const properties = {};
        const embroideryId = summary.querySelector('[data-property="_embroidery_id"]')?.dataset.propertyValue;

        // Get all property values except _embroidery_id
        summary.querySelectorAll('[data-property]:not([data-property="_embroidery_id"])').forEach(item => {
            const key = item.dataset.property;
            const value = item.dataset.propertyValue;
            if (key && value) {
                properties[key] = value;
                console.log(`Found embroidery property: ${key} = ${value}`);
            }
        });

        const data = {
            properties,
            variantId,
            embroideryId
        };

        console.log('Collected embroidery data:', data);
        return data;
    }

    async addItemsToCart(items) {
        if (!items.length) {
            console.warn('No items to add to cart');
            return null;
        }

        console.log('Sending add to cart request:', items);
        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });

            if (!response.ok) {
                throw new Error(`Cart add failed with status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Cart add response:', result);
            return result;
        } catch (error) {
            console.error('Error in addItemsToCart:', error);
            throw error;
        }
    }
}

// Product Page Controller
const ProductPageController = (() => {
    let instance;
    let galleryIndicator;
    
    return {
        init() {
            console.log('Initializing ProductPageController');
            instance = new class ProductPage {
                constructor() {
                    this.customSelect = null;
                    this.cartManager = null;
                    this.productSwitcher = null;
                    this.init();
                }

                init() {
                    this.customSelect = new CustomSelect();
                    this.cartManager = new CartManager();
                    this.productSwitcher = new ProductSwitcher();
                    if (galleryIndicator) {
                        galleryIndicator.cleanup();
                    }
                    galleryIndicator = new GalleryScrollIndicator();
                    console.log('ProductPage components initialized');
                }

                reinitialize() {
                    console.log('Reinitializing ProductPage components');
                    this.customSelect?.init();
                    this.cartManager?.bindEvents();
                    if (galleryIndicator) {
                        galleryIndicator.cleanup();
                    }
                    galleryIndicator = new GalleryScrollIndicator();
                }
            }();
        },
        reinitialize() {
            console.log('Reinitialize called on ProductPageController');
            instance?.reinitialize();
        }
    };
})();

// Initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        console.log('DOMContentLoaded - initializing ProductPageController');
        ProductPageController.init();
    });
} else {
    console.log('Document already loaded - initializing ProductPageController');
    ProductPageController.init();
}

// Expose reinitialize function
window.reinitializeProduct = ProductPageController.reinitialize;

// Listen for product switch events
window.addEventListener('productSwitched', () => {
    console.log('Product switched event received');
    window.reinitializeProduct();
});
